import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  IntegrationConfig,
  IntegrationConfiguration,
  PublicApiKey,
  PublicApiKeyCreatedResponse,
  PublicApiUsageReport,
} from '../models/integrationConfig';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MerchantService {
  constructor(private api: ApiService) {}

  unreadNotifications = 0;

  PATH = 'v1/merchant';

  getMerchant(merchant: string) {
    return this.api.get(`${this.PATH}/${merchant}`);
  }

  getMerchants(limit: number, page: number) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/list` + query);
  }

  getRecentActivity() {
    return this.api.get(`${this.PATH}/activity`);
  }

  getCustomers(
    sortKey: string,
    sortDir: string,
    limit: number,
    page: number,
    q: string,
    filters: any,
    cards = [],
    vouchers = [],
    voucherStatus = [],
  ) {
    const query =
      `?limit=${limit}&page=${page}&search=${q}&sortKey=${sortKey}&sortDir=${sortDir}&cards=${cards?.join(
        ',',
      )}&vouchers=${vouchers?.join(',')}&voucherStatus=${voucherStatus?.join(',')}` +
      Object.keys(filters)
        .map((key) => `&${key}=${filters[key]}`)
        .join('');

    return this.api.get(`${this.PATH}/customers` + query);
  }

  getSentMessages(limit: number, page: number) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/messages/sent` + query);
  }

  getBirthdayNotifications(limit: number, page: number) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/birthday-notifications` + query);
  }

  getCustomer(customer: string) {
    return this.api.get(`${this.PATH}/customer/${customer}`);
  }

  getMessage(message?: string) {
    return this.api.get(`${this.PATH}/message/${message}`);
  }

  getPlanLimits() {
    return this.api.get(`${this.PATH}/limits`);
  }

  getCustomerCards(limit: number, page: number, customer?: string) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/customer/cards/${customer}` + query);
  }

  getTeamMembers(limit: number, page: number) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/team/list` + query);
  }

  getNFCCards() {
    return this.api.get(`${this.PATH}/nfc/list`);
  }

  requestNFCCard(data: any) {
    return this.api.post(`${this.PATH}/nfc/request`, data);
  }

  getNFCStamps(nfcRef: string, limit: number, page: number, points?: boolean) {
    var query = `?limit=${limit}&page=${page}&points=${points}`;
    return this.api.get(`${this.PATH}/nfc/stamps/${nfcRef}` + query);
  }

  getNFCRedemptions(nfcRef: string, limit: number, page: number) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/nfc/redemptions/${nfcRef}` + query);
  }

  toggleNFCCard(card?: string, value?: boolean) {
    return this.api.post(`${this.PATH}/nfc/toggle/${card}`, { value: value });
  }

  updateConversationSettings(
    canBeMessage: boolean,
    emailNotifications: boolean,
    infoText?: string,
  ) {
    return this.api.post(`${this.PATH}/conversation-settings`, {
      canBeMessaged: canBeMessage,
      emailNotifications: emailNotifications,
      infoText: infoText,
    });
  }

  editNFCCard(card: string, nickname?: string, location?: string) {
    return this.api.post(`${this.PATH}/nfc/edit/${card}`, {
      nickname: nickname,
      location: location,
    });
  }

  getCustomerRewards(limit: number, page: number, customer?: string) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/customer/rewards/${customer}` + query);
  }

  getCustomerVouchers(
    limit: number,
    page: number,
    customer?: string,
    nonRedeemedOnly?: boolean,
  ) {
    var query = `?limit=${limit}&page=${page}`;
    if (nonRedeemedOnly) {
      query += '&nonRedeemedOnly=true';
    }
    return this.api.get(`${this.PATH}/customer/vouchers/${customer}` + query);
  }

  createMerchant(data: any) {
    return this.api.post(`${this.PATH}`, data);
  }

  toggleCustomerNotifications(data: any) {
    return this.api.put(`${this.PATH}/customer/notifications`, data);
  }

  giveStamp(
    userId: string,
    cardId: string,
    quantity: number,
    location?: string,
    notify?: boolean,
  ) {
    const data = {
      user: userId,
      type: 'card',
      object: cardId,
      numberOfStamps: quantity,
      location: location,
      notify: notify,
    };

    return this.api.post(`${this.PATH}/customer/stamp/add`, data);
  }

  adjustPoints(data: any) {
    return this.api.post(`${this.PATH}/customer/points/adjust`, data);
  }

  redeemReward(customerId: string, rewardId: string, location?: string) {
    return this.api.post(`${this.PATH}/customer/reward/redeem`, {
      customerId: customerId,
      rewardId: rewardId,
      location: location,
    });
  }

  redeemVoucher(voucherId: string, amount: number) {
    return this.api.post(`${this.PATH}/customer/voucher/redeem`, {
      voucherId,
      amount,
    });
  }

  assignCustomerToVoucher(
    voucherId: string,
    customerId: string,
    notify: boolean = true,
  ) {
    return this.api.post(`${this.PATH}/customer/voucher/assign`, {
      voucherId,
      customerId,
      notify,
    });
  }

  unassignCustomerFromVoucher(voucherId: string, customerId: string) {
    return this.api.post(`${this.PATH}/customer/voucher/unassign`, {
      voucherId,
      customerId,
    });
  }

  giveReward(
    customerId: string,
    rewardId: string,
    quantity: number,
    notify: boolean,
  ) {
    return this.api.post(`${this.PATH}/customer/reward/give`, {
      customerId: customerId,
      rewardId: rewardId,
      quantity: quantity,
      notify: notify,
    });
  }

  giftRewardByEmail(
    rewardId: string,
    quantity: number,
    email: string,
    sendEmail: boolean,
  ) {
    return this.api.post(`${this.PATH}/customer/reward/gift-by-email`, {
      rewardId: rewardId,
      quantity: quantity,
      email: email,
      sendEmail: sendEmail,
    });
  }

  expireReward(customerId: string, rewardId: string) {
    return this.api.post(`${this.PATH}/customer/reward/expire`, {
      customerId: customerId,
      rewardId: rewardId,
    });
  }

  updateRewardHiddenText(
    customerId: string,
    rewardId: string,
    hiddenText: string,
  ) {
    return this.api.post(`${this.PATH}/customer/reward/update-hidden-text`, {
      customerId: customerId,
      rewardId: rewardId,
      hiddenText: hiddenText,
    });
  }

  expireVoucher(customerId: string, voucherId: string) {
    return this.api.post(`${this.PATH}/customer/voucher/expire`, {
      customerId: customerId,
      voucherId: voucherId,
    });
  }

  updateVoucherExpiry(voucherId: string, expiry: Date | null) {
    return this.api.post(`${this.PATH}/customer/voucher/update-expiry`, {
      voucherId: voucherId,
      expiry: expiry,
    });
  }

  getPendingRewards(limit: number, page: number) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/pending-rewards` + query);
  }

  revertPendingReward(pendingRewardId: string) {
    return this.api.post(`${this.PATH}/pending-rewards/revert`, {
      pendingRewardId: pendingRewardId,
    });
  }

  createTeamMember(payload: any) {
    return this.api.post(`${this.PATH}/team`, payload);
  }

  removeTeamMember(teamMemberId: string) {
    return this.api.post(`${this.PATH}/team/remove/${teamMemberId}`, {});
  }

  updateTeamMember(payload: any, id?: string) {
    return this.api.post(`${this.PATH}/team/update/${id}`, payload);
  }

  activateTeamMember(id?: string) {
    return this.api.post(`${this.PATH}/team/activate/${id}`, {});
  }

  triggerInvite(id?: string) {
    return this.api.post(`${this.PATH}/team/invite/${id}`, {});
  }

  triggerActivityReport(frequency: string, userId?: string) {
    return this.api.post(`${this.PATH}/report/trigger`, {
      frequency,
      userId,
    });
  }

  sendPush(payload: any, draft: boolean) {
    let path = draft ? `${this.PATH}/push/save` : `${this.PATH}/push`;
    return this.api.post(path, payload);
  }

  sendTestPushNotification(payload: any) {
    return this.api.post(`${this.PATH}/push/test`, payload);
  }

  deleteMessage(messageId: string) {
    return this.api.delete(`${this.PATH}/push/${messageId}`);
  }

  export(
    type: string,
    from?: string,
    to?: string,
    streaming: boolean = false,
  ): Observable<any> {
    const payload = { from, to };

    if (streaming) {
      return this.api.request('POST', `${this.PATH}/export/${type}`, payload, {
        responseType: 'blob',
        observe: 'events',
        reportProgress: true,
      });
    } else {
      return this.api.post(`${this.PATH}/export/${type}`, payload);
    }
  }

  dismissNotice(notice: string) {
    return this.api.post(`${this.PATH}/dismiss`, { notice: notice });
  }

  getIntegration(type: string) {
    return this.api.get(`${this.PATH}/integration/${type}`);
  }

  getActiveIntegrations() {
    return this.api.get(`${this.PATH}/integration/active`);
  }

  getPublicApiKeys(): Observable<PublicApiKey[]> {
    return this.api.get(`${this.PATH}/api-keys`);
  }

  getPublicApiKeyUsage(days: 7 | 30 | 90): Observable<PublicApiUsageReport> {
    return this.api.get(`${this.PATH}/api-keys/usage?days=${days}`);
  }

  createPublicApiKey(payload: {
    name: string;
    scopes: string[];
    expiresAt?: string | null;
  }): Observable<PublicApiKeyCreatedResponse> {
    return this.api.post(`${this.PATH}/api-keys`, payload);
  }

  updatePublicApiKey(
    id: string,
    payload: { name: string; scopes: string[]; expiresAt?: string | null },
  ): Observable<PublicApiKey> {
    return this.api.patch(`${this.PATH}/api-keys/${id}`, payload);
  }

  rotatePublicApiKey(id: string): Observable<PublicApiKeyCreatedResponse> {
    return this.api.post(`${this.PATH}/api-keys/${id}/rotate`, {});
  }

  revokePublicApiKey(id: string): Observable<PublicApiKey> {
    return this.api.delete(`${this.PATH}/api-keys/${id}`);
  }

  updatePublicApiOrderSettings(payload: {
    awardStamps: boolean;
    inviteUsers: boolean;
    rewardAutomation: { enabled: boolean };
  }): Observable<IntegrationConfig> {
    return this.api.post(`${this.PATH}/api-order-settings`, payload);
  }

  removeIntegration(type: string) {
    return this.api.post(`${this.PATH}/integration/remove/${type}`, {});
  }

  deleteIntegrationConfiguration(id: string, type: string) {
    return this.api.post(`${this.PATH}/integration/configuration/remove`, {
      id,
      type,
    });
  }

  generateOauthUrl(type: string) {
    return this.api.get(`${this.PATH}/integration/oauth/url/${type}`);
  }

  addShopify(payload: any) {
    return this.api.post(`${this.PATH}/shopify/save`, payload);
  }

  addSquare(payload: any) {
    return this.api.post(`${this.PATH}/square/save`, payload);
  }

  addEposNow(payload: any) {
    return this.api.post(`${this.PATH}/eposnow/save`, payload);
  }

  addZapier(payload: any) {
    return this.api.post(`${this.PATH}/zapier/save`, payload);
  }

  initializeZapier() {
    return this.api.post(`${this.PATH}/zapier/initialize`, {});
  }

  addMake(payload: any) {
    return this.api.post(`${this.PATH}/make/save`, payload);
  }

  initializeMake() {
    return this.api.post(`${this.PATH}/make/initialize`, {});
  }

  addSquarespace(payload: any) {
    return this.api.post(`${this.PATH}/squarespace/save`, payload);
  }

  connectSquarespace(payload: any) {
    return this.api.post(`${this.PATH}/squarespace/connect`, payload);
  }

  verifyOauth(payload: any) {
    return this.api.post(`${this.PATH}/integration/oauth/token`, payload);
  }

  disconnectSquarespace() {
    return this.api.post(`${this.PATH}/squarespace/disconnect`, {});
  }

  addIntegrationConfiguration(body: IntegrationConfiguration, type: string) {
    const payload = {
      ...body,
      integrationType: type,
    };

    return this.api.post(`${this.PATH}/integration/configuration`, payload);
  }

  updateIntegrationConfiguration(body: IntegrationConfiguration, type: string) {
    const payload = {
      ...body,
      integrationType: type,
    };

    return this.api.post(
      `${this.PATH}/integration/configuration/update`,
      payload,
    );
  }

  getExternalOrders(limit: number, page: number, type: string) {
    var query = `?limit=${limit}&page=${page}`;
    return this.api.get(`${this.PATH}/integration/orders/${type}` + query);
  }

  syncCustomersFromIntegration(type: string) {
    return this.api.post(`${this.PATH}/integration/sync-customers/${type}`, {});
  }

  getInvites(limit: number, page: number, type: string) {
    var query = `?limit=${limit}&page=${page}&type=${type}`;
    return this.api.get(`${this.PATH}/integration/invites` + query);
  }

  updateCustomer(customerId: string, key: string, value: string) {
    return this.api.post(`${this.PATH}/customer/update/${customerId}`, {
      key: key,
      value: value,
    });
  }

  createCustomer(data: {
    email?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    cardId?: string;
  }) {
    return this.api.post(`${this.PATH}/customer/create`, data);
  }

  getAutomations(includePromotions = false) {
    return this.api.get(
      `${this.PATH}/automations?includePromotions=${includePromotions}`,
    );
  }

  getReferrals() {
    return this.api.get(`${this.PATH}/referrals`);
  }

  updateBirthdayClub(
    daysBeforeBirthday: number,
    birthdayEnabled: boolean,
    birthdayMessage: string,
    birthdayReward?: string,
    birthdayRewardType?: string,
    birthdayCard?: string,
    birthdayQuantity?: number,
  ) {
    return this.api.post(`${this.PATH}/birthday-club`, {
      daysBeforeBirthday: daysBeforeBirthday,
      birthdayEnabled: birthdayEnabled,
      birthdayMessage: birthdayMessage,
      birthdayReward: birthdayReward,
      birthdayRewardType: birthdayRewardType,
      birthdayCard: birthdayCard,
      birthdayQuantity: birthdayQuantity,
    });
  }

  getCustomerAutomations() {
    return this.api.get(`${this.PATH}/automations/list`);
  }

  createAutomation(data: any) {
    return this.api.post(`${this.PATH}/automations`, data);
  }

  updateAutomation(id: string, data: any) {
    return this.api.put(`${this.PATH}/automations/${id}`, data);
  }

  deleteAutomation(id: string) {
    return this.api.delete(`${this.PATH}/automations/${id}`);
  }

  getAutomationExecutions(automationId: string, dateFilter: string = 'all') {
    return this.api.get(
      `${this.PATH}/customer-automations/${automationId}/executions?dateFilter=${dateFilter}`,
    );
  }

  getStaffLocations() {
    return this.api.get(`${this.PATH}/staff-locations`);
  }

  getChecklistStatus() {
    return this.api.get(`${this.PATH}/onboard-status`);
  }

  createPromotion(data: any) {
    return this.api.post(`${this.PATH}/promotion`, data);
  }

  deletePromotion(promotionId: string) {
    return this.api.delete(`${this.PATH}/promotion/${promotionId}`);
  }

  updateMicrositeConfig(config: any) {
    return this.api.post(`${this.PATH}/microsite/config`, config);
  }
}
